import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import BarChart from './BarChart';
import RadarChart from './RadarChart';
import { fetchSkillStats } from '../../services/skillStatsService';
import { SkillStatsData } from '../../types';
import { formatDateTimeJST } from '../../utils/dateUtils';

interface SkillStatsSectionProps {
  employeeId: string;
}

const SkillStatsSection: React.FC<SkillStatsSectionProps> = ({ employeeId }) => {
  const [statsData, setStatsData] = useState<SkillStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchSkillStats(employeeId);
        setStatsData(response.data);
      } catch (err) {
        console.error('スキル統計の読み込みエラー:', err);
        setError(err instanceof Error ? err.message : 'スキル統計の読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    if (employeeId) {
      loadStats();
    }
  }, [employeeId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!statsData || statsData.sortedCategoryStats.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        表示可能なスキル統計データがありません
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        スキルサマリー
      </Typography>

      <Grid container spacing={3}>
        {/* 棒グラフ（左側） */}
        <Grid item xs={12} md={6}>
          <BarChart sortedCategoryStats={statsData.sortedCategoryStats} />
        </Grid>

        {/* レーダーチャート（右側） */}
        <Grid item xs={12} md={6}>
          <RadarChart radarCategoryStats={statsData.radarCategoryStats} />
        </Grid>
      </Grid>

      {/* 最終更新日時 */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          最終更新: {formatDateTimeJST(statsData.lastUpdated, true)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default SkillStatsSection;
